import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ToastService } from '../../../../shared/services/toast.service';
import { extractDjangoErrors, formatDjangoErrors } from '../../../../shared/utils/django-errors';
import { IMetadataSourceType } from '../../../metadata-mgmt/models/Metadata-mgmt';
import { MetadataMgmtService } from '../../../metadata-mgmt/service/metadata-mgmt.service';
import { ValidationRulesFacade } from '../../facade/validation-rules.facade';

/**
 * Left rail of the Rules-by-Template tab.
 *
 * Reuses `MetadataMgmtService.getAllSequence()` to source the SourceType
 * list so we don't duplicate that fetch in this feature. "Core" is pinned
 * at the top as the `null` source-type entry (matches how the backend
 * stores core templates with `source_type=NULL`).
 */
@Component({
  selector: 'app-source-type-sidebar',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './source-type-sidebar.component.html',
})
export class SourceTypeSidebarComponent implements OnInit {
  private metadataService = inject(MetadataMgmtService);
  private facade = inject(ValidationRulesFacade);
  private toast = inject(ToastService);

  sourceTypes = signal<IMetadataSourceType[]>([]);
  selectedSourceTypeId = this.facade.selectedSourceTypeIdSignal;

  // Number of rules attached to templates within each scope, used for the
  // count badge next to each entry. Core scope counts rules on templates
  // with `source_type=null`; per-source scopes filter accordingly.
  // The selectors handle this client-side from the already-loaded lists.
  private fieldRulesSignal = this.facade.fieldRulesSignal;
  private crossFieldRulesSignal = this.facade.crossFieldRulesSignal;

  countByScope = computed<Map<number | null, number>>(() => {
    // We can't bucket by source_type from FieldRules alone — those are
    // attached to a `metadata_template`. The page lacks template->source_type
    // info here; we just show a generic count from cross-field rules which
    // do carry source_type directly. Best-effort badge.
    const result = new Map<number | null, number>();
    for (const r of this.crossFieldRulesSignal()) {
      const key = r.source_type ?? null;
      result.set(key, (result.get(key) ?? 0) + 1);
    }
    return result;
  });

  ngOnInit(): void {
    this.metadataService.getAllSequence().subscribe({
      next: (rows) => {
        this.sourceTypes.set(rows ?? []);
        // Auto-select Core on first load so the panel never starts empty.
        if (this.facade.selectedSourceTypeIdSignal() === null) {
          this.facade.selectSourceType(null);
        }
      },
      error: (err) => {
        const formatted = formatDjangoErrors(extractDjangoErrors(err));
        this.toast.show(
          formatted && formatted !== 'An unexpected error occurred.'
            ? formatted
            : 'Failed to load source types',
          'error',
        );
      },
    });
  }

  select(sourceTypeId: number | null): void {
    this.facade.selectSourceType(sourceTypeId);
  }

  trackById(_: number, st: IMetadataSourceType): number {
    return st.id;
  }
}
